import {
  Box,
  Typography,
  Button,
  TextField,
  Autocomplete,
  IconButton,
} from "@mui/material";
import { useState } from "react";
import { Add, Edit, Delete } from "@mui/icons-material";
import { DefaultPrimaryButton } from "@/styles/globalstyles";
import Swal from "sweetalert2";

interface EditableProcessListProps {
  data: {
    id: number;
    name: string;
    master_process_id: number;
  }[];
  title: string;
  options: Array<{
    label: string;
    value: number;
  }>;
  handleAddProcess: (process: {
    id: number;
    name: string;
    master_process_id: number;
  }) => Promise<void>;
  handleEditProcess: (process: {
    id: number;
    name: string;
    master_process_id: number;
  }) => Promise<void>;
  handleDeleteProcess: (processId: number) => Promise<void>;
  disabled: boolean;
  setDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

const EditableProcessList = ({
  data,
  title,
  options,
  handleAddProcess,
  handleEditProcess,
  handleDeleteProcess,
  disabled,
  setDisabled,
}: EditableProcessListProps) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState<{
    id: number;
    name: string;
    master_process_id: number;
  } | null>(null);

  const resetForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData(null);
    setErrors({});
    setDisabled(false);
  };

  const handleAddClick = () => {
    setIsAdding(true);
    setDisabled(true);
    setErrors({});
  };

  const handleEditClick = (process: {
    id: number;
    name: string;
    master_process_id: number;
  }) => {
    setEditingId(process.id);
    setFormData({
      id: process.id,
      name: process.name,
      master_process_id: process.master_process_id,
    });
    setDisabled(true);
    setErrors({});
  };

  const handleDeleteClick = (processId: number) => {
    Swal.fire({
      title: "คุณต้องการลบ Process นี้หรือไม่?",
      text: "การลบ Process จะส่งผลกระทบต่อการคำนวนราคา",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    }).then((result) => {
      if (result.isConfirmed) {
        handleDeleteProcess(processId);
        setDisabled(false);
      }
    });
  };

  const handleSave = async () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData || !formData.name.trim()) {
      newErrors.name = "กรุณาเลือก Process";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    if (isAdding && formData) {
      await handleAddProcess(formData);
    } else if (editingId !== null && formData) {
      await handleEditProcess(formData);
    }
    setDisabled(false);
    resetForm();
  };

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        p: 2,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h3" mb={1}>
          {title}
        </Typography>
        {!disabled && !isAdding && !editingId && (
          <Button
            size="small"
            variant="outlined"
            sx={{ ...DefaultPrimaryButton, fontSize: "0.8rem" }}
            onClick={handleAddClick}
            startIcon={<Add />}
          >
            เพิ่ม Process
          </Button>
        )}
      </Box>
      {isAdding && !editingId && (
        <Box
          mb={3}
          p={2}
          border="2px solid #2196f3"
          borderRadius={2}
          bgcolor="#f8f9fa"
        >
          <Typography variant="subtitle1" mb={1} color="primary">
            เพิ่ม Process ใหม่
          </Typography>
          <Box display="flex" gap={2} alignItems="flex-start">
            <Box flex={1}>
              <Autocomplete
                size="small"
                fullWidth
                value={
                  options.find(
                    (opt) => opt.value === formData?.master_process_id,
                  ) || null
                }
                onChange={(_event, newValue) => {
                  setFormData((prev) => ({
                    id: prev ? prev.id : 0,
                    name: newValue ? newValue.label : "",
                    master_process_id: newValue ? newValue.value : 0,
                  }));
                }}
                options={options}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!errors.name}
                    helperText={errors.name}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        style: { fontSize: "0.875rem" },
                      },
                    }}
                  />
                )}
              />
            </Box>
            <Box display="flex" gap={1} pt={0.5}>
              <Button variant="outlined" onClick={resetForm} size="small">
                ยกเลิก
              </Button>
              <Button variant="contained" onClick={handleSave} size="small">
                เพิ่ม
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {data && data.length > 0 ? (
        data.map((process) => {
          if (editingId === process.id) {
            return (
              <Box
                key={process.id}
                mb={1}
                p={2}
                border="2px solid #ff9800"
                borderRadius={2}
                bgcolor="#fff3e0"
              >
                <Typography
                  variant="subtitle2"
                  mb={2}
                  color="#f57c00"
                  fontWeight={600}
                >
                  แก้ไข Process
                </Typography>
                <Box display="flex" gap={2} alignItems="flex-start">
                  <Box flex={1}>
                    <Autocomplete
                      size="small"
                      fullWidth
                      value={
                        options.find(
                          (opt) => opt.value === formData?.master_process_id,
                        ) || null
                      }
                      onChange={(_event, newValue) => {
                        setFormData((prev) => ({
                          id: prev ? prev.id : 0,
                          name: newValue ? newValue.label : "",
                          master_process_id: newValue ? newValue.value : 0,
                        }));
                      }}
                      options={options}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!errors.name}
                          helperText={errors.name}
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              style: {
                                fontSize: "0.875rem",
                                backgroundColor: "white",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                  <Box display="flex" gap={1} pt={0.5}>
                    <Button
                      variant="outlined"
                      sx={{ backgroundColor: "white" }}
                      onClick={resetForm}
                      size="small"
                    >
                      ยกเลิก
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      size="small"
                      sx={{
                        bgcolor: "#f57c00",
                        "&:hover": { bgcolor: "#e65100" },
                      }}
                    >
                      บันทึก
                    </Button>
                  </Box>
                </Box>
              </Box>
            );
          }
          return (
            <Box
              key={process.id}
              mb={1}
              p={2}
              border="1px solid #e0e0e0"
              borderRadius={1}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                "&:hover": {
                  bgcolor: "#f5f5f5",
                },
              }}
            >
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  {process.name}
                </Typography>
              </Box>
              {!disabled && !isAdding && !editingId && (
                <Box display="flex" gap={1}>
                  <IconButton
                    size="small"
                    onClick={() => handleEditClick(process)}
                    color="primary"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(process.id)}
                    color="error"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          );
        })
      ) : !isAdding && !editingId ? (
        <Typography variant="body2" color="textSecondary" py={2}>
          {`ไม่มีข้อมูล ${title}`}
        </Typography>
      ) : null}
    </Box>
  );
};

export default EditableProcessList;
